import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useFormatters } from '../../../utils/format';
import Icon from '../../../components/AppIcon';

const PeerComparison = ({ comparisonData }) => {
  const { t } = useTranslation();
  const { num, eur } = useFormatters();
  const [selectedMetric, setSelectedMetric] = useState('totalComp');
  const activeData = comparisonData?.[selectedMetric] || comparisonData?.totalComp || {};

  const metrics = [
    { key: 'totalComp', labelKey: 'employeePortal.peer.totalCompensation', icon: 'Wallet' },
    { key: 'baseSalary', labelKey: 'employeePortal.peer.baseSalary', icon: 'DollarSign' },
    { key: 'variablePay', labelKey: 'employeePortal.peer.variablePay', icon: 'TrendingUp' }
  ];

  const chartData = activeData?.distributions?.map(item => ({
    range: item?.range,
    count: item?.count,
    yourPosition: item?.includesYou
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-elevated">
          <p className="font-medium text-popover-foreground">{label}</p>
          <p className="text-sm text-muted-foreground">
            {t('employeePortal.peer.employeesInRange', { count: payload?.[0]?.value })}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">
          {t('employeePortal.peer.cardTitle')}
        </h2>
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="Shield" size={16} />
          <span>{t('employeePortal.peer.anonymizedData')}</span>
        </div>
      </div>

      {/* Metric Selector */}
      <div className="flex flex-wrap gap-2 mb-6">
        {metrics?.map((metric) => (
          <button
            key={metric?.key}
            onClick={() => setSelectedMetric(metric?.key)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
              selectedMetric === metric?.key
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
            }`}
          >
            <Icon name={metric?.icon} size={16} />
            <span>{t(metric.labelKey)}</span>
          </button>
        ))}
      </div>

      {/* Comparison Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Users" size={16} className="text-primary" />
            <span className="text-sm text-muted-foreground">
              {t('employeePortal.peer.sampleSize')}
            </span>
          </div>
          <p className="text-lg font-semibold text-foreground">
            {t('employeePortal.peer.employees', { count: activeData?.sampleSize })}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('employeePortal.peer.similarRoles')}
          </p>
        </div>

        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="BarChart3" size={16} className="text-accent" />
            <span className="text-sm text-muted-foreground">
              {t('employeePortal.peer.marketMedian')}
            </span>
          </div>
          <p className="text-lg font-semibold text-foreground">{eur(activeData?.marketMedian)}</p>
          <p className="text-xs text-muted-foreground">
            {t('employeePortal.peer.fiftiethPercentile')}
          </p>
        </div>

        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Target" size={16} className="text-success" />
            <span className="text-sm text-muted-foreground">
              {t('employeePortal.peer.yourPercentile')}
            </span>
          </div>
          <p className="text-lg font-semibold text-foreground">{activeData?.yourPercentile}th</p>
          <p className="text-xs text-muted-foreground">
            {t('employeePortal.peer.marketPosition')}
          </p>
        </div>
      </div>

      {/* Distribution Chart */}
      <div className="mb-6">
        <h3 className="font-medium text-foreground mb-4">
          {t('employeePortal.peer.compensationDistribution')}
        </h3>
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart key={selectedMetric} data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="range"
                tick={{ fontSize: 12, fill: 'var(--color-muted-foreground)' }}
                axisLine={{ stroke: 'var(--color-border)' }}
              />
              <YAxis
                tick={{ fontSize: 12, fill: 'var(--color-muted-foreground)' }}
                axisLine={{ stroke: 'var(--color-border)' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="count"
                fill="var(--color-primary)"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Privacy Notice */}
      <div className="bg-muted/20 border border-border rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Lock" size={16} className="text-muted-foreground mt-0.5" />
          <div>
            <h4 className="font-medium text-foreground mb-1">
              {t('employeePortal.peer.privacyTitle')}
            </h4>
            <p className="text-sm text-muted-foreground">
              {t('employeePortal.peer.privacyBody')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PeerComparison;
